/**
 * Web Push (notifications de l'agence sur le téléphone, application fermée comprise).
 * Requires the portal service worker (lib/pwa.js) and the browser notification permission.
 */
import { easerGet, easerPost } from './easerApi';

export const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const registration = async () => {
  if (!pushSupported()) return null;
  // The portal SW is registered by InstallAppCard; wait for it (or register it) so we always get a registration.
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (reg) return reg;
  try { return await navigator.serviceWorker.register('/sw.js', { scope: '/' }); } catch { return null; }
};

/** Current subscription of this device, or null. */
export const currentSubscription = async () => {
  const reg = await registration();
  if (!reg) return null;
  try { return await reg.pushManager.getSubscription(); } catch { return null; }
};

/** Ask permission (if needed), subscribe this device and register it on the server. Returns {ok, reason}. */
export const enablePush = async () => {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: perm };
  const reg = await registration();
  if (!reg) return { ok: false, reason: 'no-sw' };
  await navigator.serviceWorker.ready;
  const { data } = await easerGet('/api/easer/push/vapid-key');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.public_key) });
  const json = sub.toJSON();
  await easerPost('/api/easer/push/subscribe', { endpoint: json.endpoint, keys: json.keys, user_agent: navigator.userAgent });
  return { ok: true };
};

/** Unsubscribe this device (server + browser). */
export const disablePush = async () => {
  const sub = await currentSubscription();
  if (!sub) return { ok: true };
  try { await easerPost('/api/easer/push/unsubscribe', { endpoint: sub.endpoint }); } catch { /* server unreachable: still drop locally */ }
  try { await sub.unsubscribe(); } catch { /* already gone */ }
  return { ok: true };
};

/** Keep the server in sync when a subscription already exists on this device (called at login). */
export const syncPushSubscription = async () => {
  try {
    const sub = await currentSubscription();
    if (!sub || Notification.permission !== 'granted') return;
    const json = sub.toJSON();
    await easerPost('/api/easer/push/subscribe', { endpoint: json.endpoint, keys: json.keys, user_agent: navigator.userAgent });
  } catch { /* best effort */ }
};
